import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, hotTopics } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  const categoryArticles = articles.filter((a) => a.category === slug);
  const featuredInCategory = categoryArticles.filter((a) => a.isFeatured);
  const restInCategory = categoryArticles.filter((a) => !a.isFeatured);

  const categoryName = category?.name || '分类';
  const metaTitle = category?.metaTitle || `${categoryName} - 51CG吃瓜特色`;
  const metaDescription = category?.metaDescription || `浏览51CG吃瓜${categoryName}专区的最新文章与热门爆料。`;
  const categoryKeywords = category?.keywords || `${categoryName}, 51CG吃瓜, 热门资讯`;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName },
  ];

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: typeof window !== 'undefined' ? window.location.href : '' },
    ],
  };

  const webPageLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: metaTitle,
    url: typeof window !== 'undefined' ? window.location.href : '',
    description: metaDescription,
    isPartOf: { '@type': 'WebSite', name: '51CG吃瓜特色', url: typeof window !== 'undefined' ? window.location.origin : '' },
    about: { '@type': 'Thing', name: categoryName },
  };

  return (
    <>
      <SEOHead
        title={metaTitle}
        description={metaDescription}
        keywords={categoryKeywords}
        ogType="website"
        jsonLd={[breadcrumbLd, webPageLd]}
      />

      {/* Category Hero */}
      <section className="relative min-h-[280px] md:min-h-[360px] flex items-end overflow-hidden pt-20 md:pt-24 pb-8 md:pb-12">
        <div className="absolute inset-0">
          <img
            src={category?.image || 'https://readdy.ai/api/search-image?query=Abstract%20dark%20entertainment%20news%20background%20with%20lime%20green%20neon%20accents%20and%20mysterious%20atmosphere%2C%20modern%20digital%20art&width=1200&height=400&seq=cat-hero-fallback&orientation=landscape'}
            alt={categoryName}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <Breadcrumb items={breadcrumbItems} />
          <h1 className="text-3xl md:text-5xl font-black text-white mt-4 mb-3">
            {categoryName}
          </h1>
          <p className="text-sm md:text-base text-white/70 max-w-xl">
            {category?.description || '浏览该分类下的最新热门文章与爆料资讯。'}
          </p>
          <div className="mt-4 flex items-center gap-3 text-xs text-white/50">
            <span className="flex items-center gap-1">
              <i className="ri-article-line" />
              {category?.articleCount || categoryArticles.length} 篇文章
            </span>
            <span className="w-1 h-1 rounded-full bg-white/40" />
            <span>持续更新中</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14 bg-background-50">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="flex-1 min-w-0">
            {/* Featured */}
            {featuredInCategory.length > 0 && (
              <div className="mb-10">
                <h2 className="text-lg font-bold text-foreground-950 mb-5 flex items-center gap-2">
                  <i className="ri-vip-crown-line text-accent-500" />
                  精选推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {featuredInCategory.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </div>
            )}

            {/* All Articles */}
            <div>
              <h2 className="text-lg font-bold text-foreground-950 mb-5 flex items-center gap-2">
                <i className="ri-list-check text-accent-500" />
                全部文章
              </h2>
              {restInCategory.length > 0 ? (
                <div className="space-y-3">
                  {restInCategory.map((article) => (
                    <ArticleCard key={article.id} article={article} variant="horizontal" />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <i className="ri-inbox-line text-4xl text-foreground-300 mb-3" />
                  <p className="text-foreground-500">该分类暂无更多文章</p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1 mt-3 text-sm text-primary-600 hover:text-primary-700"
                  >
                    返回首页 <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="w-full lg:w-80 flex-shrink-0 space-y-8">
            {/* Hot Topics */}
            <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
              <h3 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500" />
                实时热榜
              </h3>
              <div className="space-y-3">
                {hotTopics.slice(0, 8).map((topic, idx) => (
                  <Link key={topic.id} to="/category/hot-gossip" className="flex items-center gap-3 group">
                    <span className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 ${idx < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'}`}>
                      {idx + 1}
                    </span>
                    <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1 flex-1">{topic.title}</span>
                    <span className="flex items-center gap-1 text-xs text-foreground-400 flex-shrink-0">
                      {topic.trend === 'up' && <i className="ri-arrow-up-line text-red-500" />}
                      {topic.trend === 'down' && <i className="ri-arrow-down-line text-green-500" />}
                      {topic.trend === 'stable' && <i className="ri-subtract-line text-foreground-400" />}
                      {topic.heat}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
              <h3 className="text-lg font-bold text-foreground-950 mb-4">其他分类</h3>
              <div className="space-y-2">
                {categories.filter((c) => c.slug !== slug).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-background-200 transition-colors group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors">{cat.name}</span>
                    <span className="text-xs text-foreground-400">{cat.articleCount}篇</span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}